import { useCallback, useEffect, useRef } from 'react'
import { useIsPage } from '@/features/shared/hooks/useIsPage'
import { useCanShowViewType } from '@/features/shared/services/ViewedElementTracker/hooks'
import { useHandleDialog } from '@/features/shared/state/Dialog/useHandleDialog'
import { DynamicFreeItemPromoModal } from '@/features/shop/components/Modal/FreeItemPromoModal/DynamicFreeItemPromoModal'
import { type PromotionMetadata } from '@/features/shop/services/Product/types'

type Params = {
  promotionMetadata: PromotionMetadata | null | undefined
  enabled?: boolean
}

// Prevents PDP and product cards from each opening the modal
let isAutoOpeningFreeItemPromoModal = false

export const useAutoOpenFreeItemPromoModal = ({ promotionMetadata, enabled = true }: Params) => {
  const shouldAttemptAutoOpenRef = useRef(false)
  const isPage = useIsPage()
  const { openDialog } = useHandleDialog()
  const [, setSeenFreeItemModal, getCanShowFreeItemModal] = useCanShowViewType('FreeItemPromoModal')

  const isAutoOpenExcludedPage =
    Boolean(isPage.GLOBAL_HOMEPAGE) ||
    Boolean(isPage.GLOBAL_SEARCH) ||
    Boolean(isPage.CART) ||
    Boolean(isPage.ORDER_DETAIL)

  const shouldPromptForReward = Boolean(promotionMetadata?.should_prompt)
  const labelCategoryId = promotionMetadata?.label_category_id
  const modalMetadata = promotionMetadata?.modal_metadata
  const promotionId = promotionMetadata?.promotion_ids?.[0]?.toString()

  useEffect(() => {
    const isEligibleFreeItem = shouldPromptForReward && labelCategoryId !== undefined
    if (
      !isEligibleFreeItem ||
      !enabled ||
      isAutoOpenExcludedPage ||
      !shouldAttemptAutoOpenRef.current
    ) {
      return
    }

    if (!getCanShowFreeItemModal()) {
      shouldAttemptAutoOpenRef.current = false
      return
    }

    if (isAutoOpeningFreeItemPromoModal) {
      shouldAttemptAutoOpenRef.current = false
      return
    }

    isAutoOpeningFreeItemPromoModal = true
    shouldAttemptAutoOpenRef.current = false

    const openFreeItemModal = async () => {
      try {
        await openDialog(DynamicFreeItemPromoModal, {
          labelCategoryId,
          promotionId,
          selectedFreeItem: undefined,
          modalMetadata,
        })
        setSeenFreeItemModal()
      } catch {
        shouldAttemptAutoOpenRef.current = true
      } finally {
        isAutoOpeningFreeItemPromoModal = false
      }
    }

    openFreeItemModal()
  }, [
    promotionId,
    shouldPromptForReward,
    labelCategoryId,
    modalMetadata,
    enabled,
    getCanShowFreeItemModal,
    isAutoOpenExcludedPage,
    openDialog,
    setSeenFreeItemModal,
  ])

  const markCartMutationTriggered = useCallback(() => {
    shouldAttemptAutoOpenRef.current = true
  }, [])

  return { markCartMutationTriggered }
}
